import { useMemo } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";

import { api } from "../lib/api";
import { requireAuth } from "../lib/requireAuth";
import { PageEyebrow } from "../components/PageEyebrow";
import { Centered } from "../components/Centered";

export const Route = createFileRoute("/progress/skill/$skillCode")({
  beforeLoad: ({ context, location }) =>
    requireAuth(context.queryClient, location.href),
  component: SkillProgressPage,
});

type ProgressEvent = {
  ts: string;
  attemptId: number;
  exerciseId: number;
  exerciseType: string;
  scaffoldingLevel: number;
  passed: boolean;
  predictedP: number;
  stabilityBefore: number;
  stabilityAfter: number;
  muBefore: number;
  muAfter: number;
  sigma2Before: number;
  sigma2After: number;
};

function SkillProgressPage() {
  const { skillCode } = Route.useParams();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["skill-progress", skillCode],
    queryFn: async () => {
      const { data, error } = await api.GET("/skills/{skill_code}/progress", {
        params: { path: { skill_code: skillCode } },
      });
      if (error) throw error;
      return data;
    },
    staleTime: 60_000,
  });

  if (isLoading) {
    return <Centered>Loading progress…</Centered>;
  }
  if (isError || !data) {
    return <Centered>Could not load progress for {skillCode}.</Centered>;
  }

  const events = (data.events ?? []) as readonly ProgressEvent[];

  return (
    <section className="mx-auto max-w-3xl px-4 sm:px-6 py-8 sm:py-12">
      <PageEyebrow
        label={
          <Link
            to="/skills"
            className="text-ink-500 hover:text-ink-900 transition-colors"
          >
            ← all skills
          </Link>
        }
        mono={skillCode}
      />
      <h1 className="font-serif text-3xl text-ink-900 mb-2 tracking-tight">
        {data.skillName}
      </h1>
      <p className="text-ink-500 text-sm mb-5 sm:mb-6 max-w-xl">
        How the engine's estimate of this skill has moved across your{" "}
        <span className="tabular-nums">{events.length}</span> attempt
        {events.length === 1 ? "" : "s"}. Reconstructed by replaying your
        attempt log through the FSRS + IRT models — nothing here is stored; the
        graphs are computed on read.
      </p>
      <div className="hairline mb-6 sm:mb-8" />

      {events.length === 0 ? (
        <p className="text-ink-500">
          You haven't attempted any exercise on this skill yet. Once you do, the
          timeline shows up here.
        </p>
      ) : (
        <div className="space-y-8">
          <ChartCard
            title="Knowledge above chance"
            subtitle="P(correct) the model would have given you just before each attempt — a 0–1 line. Greens = passed, reds = failed."
          >
            <ProbabilityChart events={events} />
          </ChartCard>
          <ChartCard
            title="Stability (memory durability)"
            subtitle="Days until P would have dropped to 90%. Grows on successful spaced reviews; shrinks on failure."
          >
            <StabilityChart events={events} />
          </ChartCard>
          <ChartCard
            title="Ability belief — μ ± σ"
            subtitle="The IRT posterior over your latent skill θ. The band tightens as the model gets more evidence."
          >
            <BeliefChart events={events} />
          </ChartCard>
          <EventTable events={events} />
        </div>
      )}
    </section>
  );
}

function ChartCard({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) {
  return (
    <div className="border border-ink-100 bg-paper p-4 sm:p-5">
      <div className="mb-3">
        <h3 className="font-serif text-base text-ink-900">{title}</h3>
        <p className="text-xs text-ink-400 mt-0.5 leading-snug">{subtitle}</p>
      </div>
      {children}
    </div>
  );
}

// Common chart dimensions. SVG `viewBox` so the chart is responsive
// inside its CSS-sized parent without a JS resize listener.
const CHART_W = 600;
const CHART_H = 120;
const PAD_L = 32;
const PAD_R = 8;
const PAD_T = 8;
const PAD_B = 18;
const PLOT_W = CHART_W - PAD_L - PAD_R;
const PLOT_H = CHART_H - PAD_T - PAD_B;

function xFor(i: number, n: number): number {
  if (n <= 1) return PAD_L + PLOT_W / 2;
  return PAD_L + (i / (n - 1)) * PLOT_W;
}

function yFor(value: number, min: number, max: number): number {
  if (max <= min) return PAD_T + PLOT_H / 2;
  const t = (value - min) / (max - min);
  return PAD_T + (1 - Math.max(0, Math.min(1, t))) * PLOT_H;
}

function ProbabilityChart({ events }: { events: readonly ProgressEvent[] }) {
  const n = events.length;
  const points = events.map(
    (e, i) => `${xFor(i, n)},${yFor(e.predictedP, 0, 1)}`,
  );
  return (
    <Chart yMin={0} yMax={1} yLabels={["1", "0.5", "0"]}>
      <polyline
        fill="none"
        stroke="#0ea5e9"
        strokeWidth="1.5"
        points={points.join(" ")}
      />
      {events.map((e, i) => (
        <circle
          key={e.attemptId}
          cx={xFor(i, n)}
          cy={yFor(e.predictedP, 0, 1)}
          r="2.5"
          fill={e.passed ? "#10b981" : "#ef4444"}
        >
          <title>
            #{e.attemptId} · {e.exerciseType} · P={e.predictedP.toFixed(2)} ·{" "}
            {e.passed ? "passed" : "failed"}
          </title>
        </circle>
      ))}
    </Chart>
  );
}

function StabilityChart({ events }: { events: readonly ProgressEvent[] }) {
  const n = events.length;
  // Clamp displayed stability range to something readable. Real values
  // can blow up into the hundreds of days after a long streak — without
  // clamping the chart compresses every recent event into a flat line.
  const maxStability = Math.max(
    1,
    ...events.map((e) => Math.min(60, e.stabilityAfter)),
  );
  const points = events.map((e, i) => {
    const v = Math.min(maxStability, e.stabilityAfter);
    return `${xFor(i, n)},${yFor(v, 0, maxStability)}`;
  });
  // Top-to-bottom: max → mid → 0. Chart renders labels at evenly spaced
  // y-positions starting from the top, so the array order has to match.
  const yLabels = [
    `${maxStability.toFixed(0)}d`,
    `${(maxStability / 2).toFixed(0)}d`,
    "0",
  ];
  return (
    <Chart yMin={0} yMax={maxStability} yLabels={yLabels}>
      <polyline
        fill="none"
        stroke="#8b5cf6"
        strokeWidth="1.5"
        points={points.join(" ")}
      />
      {events.map((e, i) => (
        <circle
          key={e.attemptId}
          cx={xFor(i, n)}
          cy={yFor(Math.min(maxStability, e.stabilityAfter), 0, maxStability)}
          r="2.5"
          fill={e.passed ? "#10b981" : "#ef4444"}
        >
          <title>
            #{e.attemptId} · S={e.stabilityAfter.toFixed(1)}d ·{" "}
            {e.passed ? "passed" : "failed"}
          </title>
        </circle>
      ))}
    </Chart>
  );
}

function BeliefChart({ events }: { events: readonly ProgressEvent[] }) {
  const n = events.length;
  // Symmetric y-range around 0 so the IRT θ axis reads naturally
  // (negative = below-average, 0 = average, positive = above). Pad
  // slightly past observed mu so the line doesn't hug the edge.
  const muMin = Math.min(-1.5, ...events.map((e) => e.muAfter - 1.5));
  const muMax = Math.max(1.5, ...events.map((e) => e.muAfter + 1.5));
  const range = [muMin, muMax] as const;

  const band = useMemo(() => {
    const top = events.map((e, i) => {
      const sigma = Math.sqrt(Math.max(0, e.sigma2After));
      return `${xFor(i, n)},${yFor(e.muAfter + sigma, range[0], range[1])}`;
    });
    const bot = events
      .map((e, i) => {
        const sigma = Math.sqrt(Math.max(0, e.sigma2After));
        return `${xFor(i, n)},${yFor(e.muAfter - sigma, range[0], range[1])}`;
      })
      .reverse();
    return [...top, ...bot].join(" ");
  }, [events, n, range]);

  const muLine = events.map(
    (e, i) => `${xFor(i, n)},${yFor(e.muAfter, range[0], range[1])}`,
  );
  return (
    <Chart
      yMin={range[0]}
      yMax={range[1]}
      yLabels={[range[1].toFixed(1), "0", range[0].toFixed(1)]}
      zeroLine
    >
      <polygon fill="#0ea5e9" fillOpacity="0.12" points={band} />
      <polyline
        fill="none"
        stroke="#0284c7"
        strokeWidth="1.5"
        points={muLine.join(" ")}
      />
      {events.map((e, i) => (
        <circle
          key={e.attemptId}
          cx={xFor(i, n)}
          cy={yFor(e.muAfter, range[0], range[1])}
          r="2"
          fill={e.passed ? "#10b981" : "#ef4444"}
        >
          <title>
            #{e.attemptId} · μ={e.muAfter.toFixed(2)} · σ=
            {Math.sqrt(Math.max(0, e.sigma2After)).toFixed(2)}
          </title>
        </circle>
      ))}
    </Chart>
  );
}

function Chart({
  yMin,
  yMax,
  yLabels,
  zeroLine = false,
  children,
}: {
  yMin: number;
  yMax: number;
  yLabels: readonly string[];
  zeroLine?: boolean;
  children: React.ReactNode;
}) {
  return (
    <svg
      viewBox={`0 0 ${CHART_W} ${CHART_H}`}
      className="w-full h-auto"
      preserveAspectRatio="none"
      role="img"
    >
      {/* y-axis labels */}
      {yLabels.map((label, i) => {
        const y = PAD_T + (i / (yLabels.length - 1)) * PLOT_H;
        return (
          <g key={label + i}>
            <line
              x1={PAD_L}
              y1={y}
              x2={CHART_W - PAD_R}
              y2={y}
              stroke="#e5e7eb"
              strokeWidth="0.5"
            />
            <text
              x={PAD_L - 4}
              y={y + 3}
              fontSize="8"
              textAnchor="end"
              fill="#9ca3af"
            >
              {label}
            </text>
          </g>
        );
      })}
      {zeroLine && (
        <line
          x1={PAD_L}
          y1={yFor(0, yMin, yMax)}
          x2={CHART_W - PAD_R}
          y2={yFor(0, yMin, yMax)}
          stroke="#9ca3af"
          strokeWidth="0.5"
          strokeDasharray="2,2"
        />
      )}
      {children}
    </svg>
  );
}

function EventTable({ events }: { events: readonly ProgressEvent[] }) {
  // Show newest-first so a learner scanning the page sees recent
  // activity at the top.
  const reversed = [...events].reverse();
  return (
    <div className="border border-ink-100 bg-paper">
      <div className="px-4 py-3 border-b border-ink-100">
        <h3 className="font-serif text-base text-ink-900">Attempts</h3>
        <p className="text-xs text-ink-400 mt-0.5">
          Each row is one attempt on an exercise tagged to this skill, newest
          first.
        </p>
      </div>
      <ul className="divide-y divide-ink-100 text-sm">
        {reversed.map((e) => (
          <li
            key={e.attemptId}
            className="px-4 py-2 flex flex-wrap items-baseline gap-x-3 gap-y-1"
          >
            <span className="text-ink-400 text-xs tabular-nums shrink-0">
              {formatTs(e.ts)}
            </span>
            <Link
              to="/progress/exercise/$exerciseId"
              params={{ exerciseId: String(e.exerciseId) }}
              className="font-mono text-xs text-ink-700 hover:text-ink-900"
            >
              #{e.exerciseId}
            </Link>
            <span className="text-ink-400 text-xs">{e.exerciseType}</span>
            <span
              className={`text-xs font-medium ${
                e.passed ? "text-emerald-700" : "text-rose-700"
              }`}
            >
              {e.passed ? "passed" : "failed"}
            </span>
            <span className="text-ink-400 text-xs tabular-nums">
              P {e.predictedP.toFixed(2)} → S {e.stabilityAfter.toFixed(1)}d · μ{" "}
              {e.muAfter.toFixed(2)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function formatTs(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
